<template>
  <div class="chat-container col-12 q-mt-md">
    <div class="chat-messages" ref="chatMessagesRef">
      <div
        v-for="(message, index) in chatMessages"
        :key="index"
        :class="[
          'message-item',
          message.sender === 'user' ? 'user-message' : 'ai-message',
        ]"
      >
        <div class="message-content" v-if="message.sender === 'user'">
          <div class="user-message-wrapper">
            <div class="user-message-text">
              {{ message.content }}
            </div>
            <q-btn
              flat
              round
              dense
              icon="event"
              color="white"
              size="md"
              class="schedule-btn q-ml-sm"
              @click="handleAgendamento(message.content)"
            >
              <q-tooltip>Agendar esta pergunta</q-tooltip>
            </q-btn>
          </div>
        </div>
        <div class="message-content" v-else>
          <template
            v-if="
              typeof message.content === 'object' && message.content.isTable
            "
          >
            <q-markup-table flat bordered class="q-my-sm">
              <thead>
                <tr>
                  <th
                    v-for="(value, key) in message.content.data[0]"
                    :key="key"
                    class="text-left"
                  >
                    {{ key }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in message.content.data"
                  :key="rowIndex"
                >
                  <td
                    v-for="(cellValue, cellKey) in row"
                    :key="cellKey"
                    class="text-left"
                  >
                    {{ cellValue }}
                  </td>
                </tr>
              </tbody>
            </q-markup-table>
          </template>
          <template v-else>
            {{ message.content }}
          </template>

          <!-- Sistema de avaliação por estrelas -->
          <div class="rating-container q-mt-sm" v-if="!message.rated">
            <div class="rating-text q-mb-xs">
              Como você avalia esta resposta?
            </div>
            <div class="rating-stars">
              <q-btn
                v-for="star in 5"
                :key="star"
                flat
                round
                size="sm"
                :icon="
                  star <= (message.hoverRating || 0) ? 'star' : 'star_border'
                "
                :color="star <= (message.hoverRating || 0) ? 'amber' : 'grey-5'"
                @click="rateResponse(message, star)"
                @mouseenter="message.hoverRating = star"
                @mouseleave="message.hoverRating = 0"
                class="rating-star"
              />
            </div>
          </div>

          <!-- Mensagem de agradecimento após avaliação -->
          <div class="rating-thanks q-mt-sm" v-if="message.rated">
            <q-icon name="check_circle" color="positive" size="sm" />
            <span class="q-ml-xs text-positive"
              >Obrigado pela sua avaliação!</span
            >
          </div>
        </div>
        <div class="message-time">{{ message.time }}</div>
      </div>
    </div>

    <div class="chat-input q-mt-md">
      <q-input
        v-model="userMessage"
        outlined
        :placeholder="
          !assistenteAtivado
            ? 'Clique em Entendi para ativar o assistente'
            : 'Digite sua mensagem...'
        "
        bg-color="white"
        class="chat-input-field"
        @keyup.enter="sendMessage"
        hide-bottom-space
        :disable="isLoading || !assistenteAtivado"
      >
        <template v-slot:append>
          <q-btn
            round
            dense
            flat
            icon="event_note"
            color="primary"
            size="md"
            @click="$emit('abrirGestaoAgendamentos')"
            class="q-mr-xs agendamento-btn"
          >
            <q-tooltip>Meus Agendamentos</q-tooltip>
          </q-btn>
          <q-btn
            round
            dense
            flat
            icon="send"
            color="primary"
            @click="sendMessage"
            :loading="isLoading"
            :disable="!assistenteAtivado"
          />
        </template>
      </q-input>
    </div>

    <div class="suggested-questions q-mt-md">
      <div class="text-h6 q-mb-md">Exemplos de interação</div>
      <div class="suggested-buttons-container">
        <q-btn
          v-for="(question, index) in suggestedQuestions"
          :key="index"
          outline
          color="primary"
          class="suggested-btn q-mb-sm"
          @click="askSuggestedQuestion(question)"
          :disable="isLoading || !assistenteAtivado"
        >
          {{ question }}
        </q-btn>
      </div>
    </div>

    <div class="suggested-questions q-mt-md">
      <div class="text-h6 q-mb-md">Dúvidas ou sugestões de uso</div>
      <div class="feedback-form q-mb-md">
        <q-input
          v-model="feedbackMessage"
          outlined
          type="textarea"
          placeholder="Digite sua dúvida, sugestão ou comentário sobre a ferramenta..."
          bg-color="white"
          class="feedback-input"
          rows="4"
          maxlength="500"
          counter
          hide-bottom-space
        >
          <template v-slot:append>
            <q-btn
              round
              dense
              flat
              icon="send"
              color="primary"
              @click="sendFeedback"
              :loading="isSendingFeedback"
              :disable="!feedbackMessage.trim()"
              class="feedback-send-btn"
            />
          </template>
        </q-input>
      </div>
      <div class="feedback-contact-info">
        Ou entre em contato conosco diretamente:
        <a href="mailto:suporte@multpex.com.br" class="text-primary"
          >suporte@multpex.com.br</a
        >
        ou no nosso
        <a
          href="https://wa.me/5511918217360"
          target="_blank"
          class="text-primary"
          >WhatsApp oficial</a
        >.
      </div>
    </div>

    <!-- Modal de Agendamento -->
    <AgendamentoModal
      v-model="mostrarAgendamento"
      :pergunta="perguntaParaAgendar"
      :cliente="clienteObj"
      @agendado="onPerguntaAgendada"
    />
  </div>
</template>

<script>
import { ref, nextTick } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";
import {
  sendToAIEndpoint,
  sendRatingToAPI,
  sendFeedbackToAPI,
  generateRandomId,
} from "../services/iaApiService";
import AgendamentoModal from "@/modules/assistenteIA/components/AgendamentoModal.vue";

export default {
  name: "ChatIA",
  components: {
    AgendamentoModal,
  },
  emits: ["abrirGestaoAgendamentos"],
  props: {
    assistenteAtivado: {
      type: Boolean,
      default: false,
    },
    cliente: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const $q = useQuasar();
    const store = useStore();

    // Chat functionality
    const chatMessages = ref([
      {
        sender: "ai",
        content:
          "Olá! Sou sua assistente virtual. Se você já clicou em 'Entendi' no banner acima, pode começar a me fazer perguntas!",
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
    const userMessage = ref("");
    const chatMessagesRef = ref(null);
    const conversationId = ref(generateRandomId());
    const isLoading = ref(false);

    // Feedback functionality
    const feedbackMessage = ref("");
    const isSendingFeedback = ref(false);

    // Agendamento functionality
    const mostrarAgendamento = ref(false);
    const perguntaParaAgendar = ref("");
    const clienteObj = ref({
      nome: props.cliente || "",
      email: "",
    });

    const suggestedQuestions = ref([
      "Em quais assuntos você consegue me auxiliar agora?",
      "Me traga o TOP 5 itens mais consumidos nos últimos 30 dias, com as informações de código, descrição do item e a quantidade consumida.",
      "Quais pessoas devem trocar o EPI hoje? Me traga em uma coluna o nome das pessoas, na outra o nome do EPI e mais uma com a data, no formato brasileiro, que deveria ser trocado.",
      "Me traga o top 5 grupos de produto com maior número de pessoas com troca atrasada. Me traga o nome do grupo e a quantidade de pessoas com atraso de troca.",
    ]);

    // Função para rolar para o final do chat
    const scrollToBottom = () => {
      nextTick(() => {
        if (chatMessagesRef.value) {
          chatMessagesRef.value.scrollTop = chatMessagesRef.value.scrollHeight;
        }
      });
    };

    // Função para enviar mensagem e receber resposta da IA
    const sendMessage = async () => {
      if (!userMessage.value.trim() || isLoading.value) return;

      // Verificar se o assistente foi ativado
      if (!props.assistenteAtivado) {
        $q.dialog({
          title: "Assistente Virtual",
          message: 'Você precisa clicar em "Entendi" para ativar o assistente.',
          color: "primary",
          ok: "OK",
          persistent: true,
        });
        return;
      }

      isLoading.value = true;

      // Adiciona mensagem do usuário ao chat
      const currentTime = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      const userMessageText = userMessage.value.trim();

      chatMessages.value.push({
        sender: "user",
        content: userMessageText,
        time: currentTime,
      });

      // Limpa o input após adicionar a mensagem
      userMessage.value = "";

      // Scroll para o final do chat
      scrollToBottom();

      try {
        // Solicita resposta da API
        const aiResponse = await sendToAIEndpoint(
          userMessageText,
          conversationId.value,
          props.cliente
        );

        // Adiciona resposta da IA ao chat
        chatMessages.value.push({
          sender: "ai",
          content: aiResponse.content,
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          question: userMessageText, // Armazena a pergunta para a avaliação
          query: aiResponse.query, // Armazena a query gerada para a avaliação
          rated: false, // Flag para controlar se já foi avaliada
          hoverRating: 0, // Para efeito hover nas estrelas
        });
      } catch (error) {
        // Tratamento de erro
        console.error("Erro ao processar mensagem:", error);

        chatMessages.value.push({
          sender: "ai",
          content:
            "Desculpe, ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.",
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          query: null, // Sem query em caso de erro
        });
      } finally {
        isLoading.value = false;
        // Scroll para o final do chat após resposta
        scrollToBottom();
      }
    };

    // Função para fazer perguntas sugeridas
    const askSuggestedQuestion = (question) => {
      // Verificar se o assistente foi ativado antes de permitir o envio
      if (!props.assistenteAtivado) {
        $q.dialog({
          title: "Assistente Virtual",
          message: 'Você precisa clicar em "Entendi" para ativar o assistente.',
          color: "primary",
          ok: "OK",
          persistent: true,
        });
        return;
      }
      userMessage.value = question;
      sendMessage();
    };

    // Função para avaliar resposta da IA
    const rateResponse = async (message, rating) => {
      try {
        // Dados da avaliação
        const ratingData = {
          tenant: props.cliente, // Slug do cliente
          usuario_email:
            store.state.autenticacao.usuarioLogado?.email ||
            "email_nao_disponivel",
          conversation_id: conversationId.value,
          pergunta: message.question,
          resposta: (() => {
            const fullResponse =
              typeof message.content === "object"
                ? JSON.stringify(message.content)
                : message.content;
            // Limita a resposta a 100 caracteres
            return fullResponse.length > 100
              ? fullResponse.substring(0, 100) + "..."
              : fullResponse;
          })(),
          query: message.query, // Inclui a query gerada pela IA
          nota: rating,
          timestamp: new Date().toISOString(),
        };

        // Enviar avaliação
        await sendRatingToAPI(ratingData);

        // Marcar mensagem como avaliada
        message.rated = true;
        message.rating = rating;

        // Mostrar notificação de sucesso
        $q.notify({
          message: "Avaliação enviada com sucesso!",
          color: "positive",
          position: "top",
          timeout: 2000,
          icon: "check_circle",
        });
      } catch (error) {
        // Mostrar notificação de erro
        $q.notify({
          message: "Erro ao enviar avaliação. Tente novamente.",
          color: "negative",
          position: "top",
          timeout: 3000,
          icon: "error",
        });
      }
    };

    // Função para enviar feedback/dúvidas/sugestões
    const sendFeedback = async () => {
      if (!feedbackMessage.value.trim()) return;

      try {
        isSendingFeedback.value = true;

        // Dados do feedback
        const feedbackData = {
          tenant: props.cliente, // Slug do cliente
          usuario:
            store.state.autenticacao.usuarioLogado?.nome || "usuario_anonimo",
          email: store.state.autenticacao.usuarioLogado?.email || null,
          tipo: "feedback",
          mensagem: feedbackMessage.value.trim(),
          timestamp: new Date().toISOString(),
          conversation_id: conversationId.value,
        };

        // Enviar feedback
        await sendFeedbackToAPI(feedbackData);

        // Limpar campo de feedback
        feedbackMessage.value = "";

        // Mostrar notificação de sucesso
        $q.notify({
          message:
            "Feedback enviado com sucesso! Obrigado pela sua contribuição.",
          color: "positive",
          position: "top",
          timeout: 3000,
          icon: "check_circle",
        });
      } catch (error) {
        // Mostrar notificação de erro
        $q.notify({
          message: "Erro ao enviar feedback. Tente novamente mais tarde.",
          color: "negative",
          position: "top",
          timeout: 3000,
          icon: "error",
        });
      } finally {
        isSendingFeedback.value = false;
      }
    };

    // Métodos para agendamento
    const abrirAgendamento = (pergunta) => {
      console.log("Abrindo agendamento para:", pergunta);
      perguntaParaAgendar.value = pergunta;
      mostrarAgendamento.value = true;
    };

    const handleAgendamento = (pergunta) => {
      abrirAgendamento(pergunta);
    };

    const onPerguntaAgendada = (dadosAgendamento) => {
      console.log("Pergunta agendada:", dadosAgendamento);
      $q.notify({
        type: "positive",
        message: "Pergunta agendada com sucesso!",
        caption: `${dadosAgendamento.pergunta} foi agendada para ${dadosAgendamento.dataHora}`,
        position: "top",
        timeout: 4000,
        icon: "schedule",
      });
    };

    return {
      chatMessages,
      userMessage,
      chatMessagesRef,
      suggestedQuestions,
      sendMessage,
      askSuggestedQuestion,
      isLoading,
      rateResponse,
      feedbackMessage,
      isSendingFeedback,
      sendFeedback,
      mostrarAgendamento,
      perguntaParaAgendar,
      clienteObj,
      abrirAgendamento,
      handleAgendamento,
      onPerguntaAgendada,
    };
  },
};
</script>

<style scoped>
/* Chat styles */
.chat-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  background-color: #f8f9fa;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 8px;
  background-color: white;
}

.message-item {
  margin-bottom: 15px;
  padding: 10px 15px;
  border-radius: 18px;
  max-width: 80%;
  position: relative;
  word-break: break-word;
}

.user-message {
  background-color: #1976d2;
  color: white;
  align-self: flex-end;
  margin-left: auto;
}

.user-message-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.user-message-text {
  flex: 1;
}

.schedule-btn {
  transition: opacity 0.2s ease;
}

.user-message:hover .schedule-btn {
  opacity: 1 !important;
}

.ai-message {
  background-color: #e6e6e6;
  color: #333;
  align-self: flex-start;
  margin-right: auto;
}

.message-time {
  font-size: 11px;
  margin-top: 5px;
  opacity: 0.7;
  text-align: right;
}

.chat-input {
  display: flex;
  align-items: center;
}

.chat-input-field {
  flex: 1;
}

.suggested-questions {
  margin-top: 15px;
}

.suggested-buttons-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.suggested-btn {
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  white-space: normal;
  word-wrap: break-word;
  min-height: 40px;
  padding: 8px 16px;
}

/* Rating system styles */
.rating-container {
  border-top: 1px solid #e0e0e0;
  padding-top: 10px;
  margin-top: 10px;
}

.rating-text {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.rating-stars {
  display: flex;
  gap: 2px;
}

.rating-star {
  min-width: 28px;
  height: 28px;
  transition: all 0.2s ease;
}

.rating-star:hover {
  transform: scale(1.15);
}

.rating-thanks {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #f0f9f0;
  border-radius: 6px;
  border-left: 3px solid #4caf50;
  font-size: 12px;
}

/* Feedback form styles */
.feedback-form {
  margin-top: 15px;
}

.feedback-input {
  border-radius: 8px;
}

.feedback-input .q-field__control {
  background-color: white;
}

.feedback-send-btn {
  margin-left: 8px;
}

.feedback-contact-info {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin-top: 10px;
}

@media (max-width: 1211px) {
  .chat-container {
    max-width: 95%;
    width: 95%;
  }
}

@media (max-width: 838px) {
  .chat-container {
    max-width: 98%;
    width: 98%;
    padding: 15px;
  }

  .suggested-questions {
    display: flex;
    flex-direction: column;
  }

  .suggested-buttons-container {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .suggested-btn {
    font-size: 12px;
    min-height: 36px;
    padding: 6px 12px;
  }

  /* Mobile rating styles */
  .rating-star {
    min-width: 24px;
    height: 24px;
  }

  .rating-text {
    font-size: 11px;
  }

  .rating-thanks {
    font-size: 11px;
    padding: 6px 10px;
  }

  /* Mobile feedback styles */
  .feedback-input {
    font-size: 14px;
  }

  .feedback-contact-info {
    font-size: 12px;
  }

  .feedback-send-btn {
    margin-left: 4px;
  }
}

@media (max-width: 300px) {
  .chat-container {
    max-width: 100%;
    width: 100%;
    padding: 10px;
  }
}

.agendamento-btn {
  transition: all 0.3s ease;
}

.agendamento-btn:hover {
  background-color: rgba(25, 118, 210, 0.1);
  transform: scale(1.1);
}

.agendamento-btn .q-btn__content {
  font-size: 1.2em;
}

.schedule-btn {
  transition: all 0.3s ease;
  opacity: 0.8;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.schedule-btn:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.5);
  transform: scale(1.1);
}

.user-message-wrapper:hover .schedule-btn {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.15);
}
</style>
